import { Button } from "@/components/ui/Button";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useFlightDataCtx } from "@/contexts/FlightDataContext";
import { useFlightSelection } from "@/contexts/FlightSelectionContext";
import { useModal } from "@/contexts/ModalContext";
import { useTripMode } from "@/contexts/TripModeContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { usePathname, useRouter } from "@/i18n/navigation";
import { getAirlineLogoUrl } from "@/utils/airlineLogoHelper";
import { formatCurrency } from "@/utils/currencyFormatter";
import { isLegOptionsList } from "@/utils/flightResponseStructureHelper";
import { dateFormats, normalizeFlightDuration } from "@/utils/dateFormatter";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useCallback } from "react";

export default function FlightTicketCard({
  flight,
  isRoundTrip = false,
  onCardClickOverride,
  onAnalyticsClick,
}) {
  const locale = useLocale();
  const t = useTranslationDetector("flight");
  const { currency } = useCurrency();
  const isMobile = useIsMobile();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Context'lerin mevcut olup olmadığını kontrol et
  let openModal = null;
  let rawFlights = null;
  let responseStructure = null;
  let isShowingReturnFlights = false;
  let selectDepartureFlight = null;
  let selectedDepartureFlight = null;
  let isMultiCity = false;

  try {
    const modalContext = useModal();
    openModal = modalContext.openModal;
  } catch (error) {
    // ModalContext mevcut değil - sessizce devam et
  }

  try {
    const flightDataContext = useFlightDataCtx();
    rawFlights = flightDataContext.rawFlights;
    responseStructure = flightDataContext.responseStructure;
    isShowingReturnFlights = flightDataContext.isShowingReturnFlights;
  } catch (error) {
    // FlightDataContext mevcut değil - sessizce devam et
  }

  try {
    const flightSelectionContext = useFlightSelection();
    selectDepartureFlight = flightSelectionContext.selectDepartureFlight;
    selectedDepartureFlight = flightSelectionContext.selectedDepartureFlight;
  } catch (error) {
    // FlightSelectionContext mevcut değil - sessizce devam et
  }

  try {
    const tripModeContext = useTripMode();
    isMultiCity = tripModeContext.isMultiCity;
  } catch (error) {
    // TripModeContext mevcut değil - sessizce devam et
  }

  // URL'den şehir bilgilerini parse et - handleSearchClick mantığına uygun
  const parseCityInfoFromUrl = useCallback((urlToParse) => {
    if (!urlToParse) {
      return { parsedOrigin: "", parsedDestination: "" };
    }

    // URL'den pathname'i al
    let pathToParse = urlToParse;
    try {
      const urlObj = new URL(urlToParse);
      pathToParse = urlObj.pathname;
    } catch {
      // URL değilse direkt pathname olarak kullan
      pathToParse = urlToParse.startsWith("/") ? urlToParse : `/${urlToParse}`;
    }

    const pathSegments = pathToParse.split("/").filter(Boolean);

    // flight veya ucak-bileti segment'ini bul
    const flightIndex = pathSegments.findIndex(
      (segment) => segment === "ucak-bileti" || segment === "flight",
    );

    let parsedOrigin = "";
    let parsedDestination = "";

    // Eğer flight/ucak-bileti segment'i bulunamazsa, direkt path segment'lerinden parse et
    // Bu durumda ilk segment route type, ikinci segment airportInfo olabilir
    if (flightIndex === -1 && pathSegments.length >= 2) {
      const routeSegment = pathSegments[1]; // İkinci segment airportInfo olmalı

      if (routeSegment && routeSegment.includes("-")) {
        const cities = routeSegment.split("-");

        if (cities.length >= 2) {
          const lastSegment = cities[cities.length - 1];
          const secondLastSegment = cities[cities.length - 2];

          // Havalimanı kodlarını kontrol et ve çıkar
          if (
            lastSegment.length === 3 &&
            secondLastSegment.length === 3 &&
            lastSegment === lastSegment.toUpperCase() &&
            secondLastSegment === secondLastSegment.toUpperCase()
          ) {
            const citySegments = cities.slice(0, -2);

            if (citySegments.length >= 2) {
              parsedDestination = citySegments[citySegments.length - 1];
              parsedOrigin = citySegments.slice(0, -1).join("-");
            }
          } else {
            parsedDestination = cities[cities.length - 1];
            parsedOrigin = cities.slice(0, -1).join("-");
          }
        }
      }
    } else if (flightIndex !== -1 && flightIndex + 1 < pathSegments.length) {
      let routeSegmentIndex = flightIndex + 1;
      let routeSegment = pathSegments[routeSegmentIndex];

      // Eğer sonraki segment route type ise (tek-yon, one-way, gidis-donus, round-trip), onu atla
      const routeTypes = [
        "tek-yon",
        "one-way",
        "gidis-donus",
        "round-trip",
        "coklu-ucus",
        "multi-city",
      ];
      if (routeTypes.includes(routeSegment)) {
        routeSegmentIndex = flightIndex + 2;
        if (routeSegmentIndex < pathSegments.length) {
          routeSegment = pathSegments[routeSegmentIndex];
        } else {
          routeSegment = null;
        }
      }

      // Eğer route segment'i varsa ve içinde "-" varsa parse et
      if (routeSegment && routeSegment.includes("-")) {
        const cities = routeSegment.split("-");

        // Bazı şehir isimleri kendi içinde "-" içerebilir (örn: "sabiha-gokcen")
        // Havalimanı kodları genellikle 3 karakterdir (IST, AYT, vb.)
        // Son 2 segment havalimanı kodu olabilir, geri kalanı şehir isimleri
        if (cities.length >= 2) {
          // Son 2 segment'i kontrol et - eğer 3 karakter ise havalimanı kodu olabilir
          const lastSegment = cities[cities.length - 1];
          const secondLastSegment = cities[cities.length - 2];

          // Eğer son 2 segment 3 karakter ise (havalimanı kodu), onları çıkar
          if (
            lastSegment.length === 3 &&
            secondLastSegment.length === 3 &&
            lastSegment === lastSegment.toUpperCase() &&
            secondLastSegment === secondLastSegment.toUpperCase()
          ) {
            // Son 2 segment havalimanı kodu, geri kalanı şehir isimleri
            const citySegments = cities.slice(0, -2);

            if (citySegments.length >= 2) {
              // Son şehir destination, geri kalanı origin
              parsedDestination = citySegments[citySegments.length - 1];
              parsedOrigin = citySegments.slice(0, -1).join("-");
            } else if (citySegments.length === 1) {
              // Tek şehir segment'i varsa, bu hem origin hem destination olabilir
              // Ama genellikle bu durumda parse edilemez, boş bırak
              parsedOrigin = "";
              parsedDestination = "";
            }
          } else {
            // Havalimanı kodu yok, sadece şehir isimleri
            parsedDestination = cities[cities.length - 1];
            parsedOrigin = cities.slice(0, -1).join("-");
          }
        }
      }
    }
    return { parsedOrigin, parsedDestination };
  }, []);

  // Listing URL oluşturma helper fonksiyonu - handleSearchClick mantığına uygun
  const buildListingUrl = useCallback(
    (flightUrl, departureDate) => {
      if (!flightUrl || !departureDate) {
        return null;
      }

      // URL'den şehir bilgilerini parse et
      const { parsedOrigin, parsedDestination } =
        parseCityInfoFromUrl(flightUrl);

      if (!parsedOrigin || !parsedDestination) {
        return null;
      }

      // URL oluştur - sadece şehir isimlerini kullan
      const airportInfo = `${parsedOrigin}-${parsedDestination}`;
      const depDate = departureDate; // YYYY-MM-DD formatında

      // Locale'e göre URL path'ini belirle
      const oneWayPath =
        locale === "tr" ? "ucak-bileti/tek-yon" : "flight/one-way";

      // Query parametreleri oluştur - mevcut query parametrelerini koru
      const queryParams = new URLSearchParams(searchParams.toString());

      // Adult bilgisini ekle (varsayılan 1)
      queryParams.set("adult", "1");

      const queryString = queryParams.toString();
      const baseUrl = `/${oneWayPath}/${airportInfo}/${depDate}`;
      const flightLink = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      return flightLink;
    },
    [parseCityInfoFromUrl, locale, searchParams],
  );

  // Reservation URL oluşturma helper fonksiyonu - handleSearchClick mantığına uygun
  const buildReservationUrl = useCallback(
    (reservationId) => {
      if (!reservationId) return null;

      // Locale'e göre URL path'ini belirle
      // Türkçe: /ucak-bileti/rezervasyon, diğer diller: /flight/reservation
      const reservationPath =
        locale === "tr" ? "ucak-bileti/rezervasyon" : "flight/reservation";

      // Query parametreleri oluştur - mevcut query parametrelerini koru
      const queryParams = new URLSearchParams(searchParams.toString());

      const queryString = queryParams.toString();
      const baseUrl = `/${reservationPath}/${String(reservationId)}`;
      const reservationLink = queryString
        ? `${baseUrl}?${queryString}`
        : baseUrl;

      return reservationLink;
    },
    [searchParams, locale],
  );

  // Combined flight paket seçimi için helper function
  const handleCombinedFlightPackageSelection = useCallback(
    (combinedFlight) => {
      if (!combinedFlight || !combinedFlight.combinedFlights) {
        return;
      }

      const departureFlight = combinedFlight.combinedFlights?.departure;
      const returnFlight = combinedFlight.combinedFlights?.return;
      const otherFlight = combinedFlight.combinedFlights?.other;

      // Her leg için hem IsPackageExist hem de PackageDetails uzunluğu kontrolü
      const departureIsPackageExist = departureFlight?.IsPackageExist === true;
      const departureHasPackages =
        (departureFlight?.PackageDetails || []).length > 0;

      const returnIsPackageExist = returnFlight?.IsPackageExist === true;
      const returnHasPackages = (returnFlight?.PackageDetails || []).length > 0;

      const otherIsPackageExist = otherFlight?.IsPackageExist === true;
      const otherHasPackages = (otherFlight?.PackageDetails || []).length > 0;

      // LegOptionsList yapısında sıralı paket seçimi
      if (isLegOptionsList(responseStructure)) {
        // Sadece paketi olan segment'leri filtrele
        const segments = [];
        if (
          departureFlight &&
          departureIsPackageExist &&
          departureHasPackages
        ) {
          segments.push(departureFlight);
        }
        if (returnFlight && returnIsPackageExist && returnHasPackages) {
          segments.push(returnFlight);
        }
        if (otherFlight && otherIsPackageExist && otherHasPackages) {
          segments.push(otherFlight);
        }

        // İlk segment'te paket varsa modal aç
        if (segments.length > 0) {
          const flightWithPackages = {
            ...combinedFlight,
            PackageDetails: segments[0]?.PackageDetails || [],
            currentSegmentIndex: 0,
            segments: segments,
          };
          openModal(flightWithPackages);
          return;
        }
      }

      // Eğer hiçbir segment'te paket yoksa (hem IsPackageExist hem de PackageDetails kontrolü) direkt reservation
      const hasAnyPackages =
        (departureIsPackageExist && departureHasPackages) ||
        (returnIsPackageExist && returnHasPackages) ||
        (otherIsPackageExist && otherHasPackages);

      if (!hasAnyPackages) {
        const reservationId =
          combinedFlight?.ReservationId ||
          combinedFlight?.reservationId ||
          combinedFlight?.Id ||
          combinedFlight?.id;
        if (reservationId) {
          const reservationLink = buildReservationUrl(reservationId);
          if (reservationLink) {
            router.push(reservationLink, undefined, { scroll: true });
          }
        }
      }
    },
    [openModal, responseStructure, router, buildReservationUrl],
  );

  // Paket seçim logic'i - FlightListingPageTicketCard'dan alınan logic
  const handleCardClick = useCallback(() => {
    onAnalyticsClick?.(flight);

    // Eğer onCardClickOverride prop'u varsa, direkt onu kullan
    if (onCardClickOverride) {
      onCardClickOverride();
      return;
    }

    if (document.body.dataset.modalOpen === "1") return; // Modal açıkken tıklamayı engelle

    // Context'ler mevcut değilse paket kontrolü yap ve URL'ye yönlendir
    if (!openModal || !rawFlights) {
      // Flight'ın kendi paket bilgilerini kontrol et
      // IsPackageExist kontrolü
      const isPackageExist = flight?.IsPackageExist === true;

      // Paket varsa veya yoksa direkt URL'ye yönlendir
      if (flight.Url) {
        // Tarih bilgisini al
        const departureDate = flight.OutgoingDepartureDateTime
          ? dateFormats.iso(flight.OutgoingDepartureDateTime)
          : null;

        if (departureDate) {
          // handleSearchClick mantığına uygun URL oluştur
          const listingUrl = buildListingUrl(flight.Url, departureDate);
          if (listingUrl) {
            router.push(listingUrl, undefined, { scroll: true });
          } else {
            // Parse edilemezse fallback olarak orijinal URL'i kullan
            window.location.href = flight.Url;
          }
        } else {
          // Tarih yoksa fallback olarak orijinal URL'i kullan
          window.location.href = flight.Url;
        }
      }
      return;
    }

    // Round-trip ve DepartureFlights.Items yapısında ise departure flight seç
    if (
      responseStructure === "DepartureFlights.Items" &&
      !isShowingReturnFlights &&
      selectDepartureFlight
    ) {
      // Paket kontrolü: Hem IsPackageExist hem de PackageDetails uzunluğu kontrolü
      const isPackageExist = flight?.IsPackageExist === true;
      const packageDetails = flight?.PackageDetails || [];
      const hasPackageDetails = packageDetails.length > 0;

      // Paket varsa modal aç, yoksa departure flight seç
      if (isPackageExist && hasPackageDetails) {
        openModal(flight);
        return;
      }

      // Paket yoksa departure flight seç
      if (isMobile) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      selectDepartureFlight(flight);
      return;
    }

    // Return flight seçimi (round-trip)
    if (isShowingReturnFlights && selectedDepartureFlight) {
      // Return flight için paket kontrolü: Hem IsPackageExist hem de PackageDetails uzunluğu kontrolü
      const isPackageExist = flight?.IsPackageExist === true;
      const packageDetails = flight?.PackageDetails || [];
      const hasPackageDetails = packageDetails.length > 0;

      // Paket varsa modal aç, yoksa direkt reservation'a git
      if (isPackageExist && hasPackageDetails) {
        const returnFlightWithDepartureInfo = {
          ...flight,
          departureFlight: selectedDepartureFlight,
        };
        openModal(returnFlightWithDepartureInfo);
        return;
      }

      // Paket yoksa direkt reservation'a git
      const flightId = flight?.Id || flight?.id;
      const reservationId =
        flight?.ReservationId || flight?.reservationId || flightId;
      if (reservationId) {
        const reservationLink = buildReservationUrl(reservationId);
        if (reservationLink) {
          router.push(reservationLink, undefined, { scroll: true });
        }
      }
      return;
    }

    // LegOptionsList seçimi
    if (isLegOptionsList(responseStructure)) {
      // LegOptionsList için paket kontrolü
      const combinedFlightData =
        flight?.combinedFlight || flight?.CombinedFlight;
      if (combinedFlightData && Array.isArray(combinedFlightData)) {
        const combinedFlight = {
          ...flight,
          combinedFlights: combinedFlightData,
        };
        handleCombinedFlightPackageSelection(combinedFlight);
        return;
      }
    }

    // Paket kontrolü: IsPackageExist kontrolü
    const isPackageExist = flight?.IsPackageExist === true;

    // Paket varsa modal aç, yoksa reservation'a git
    if (isPackageExist) {
      openModal(flight);
    } else {
      const flightId = flight?.Id || flight?.id;
      const reservationId =
        flight?.ReservationId || flight?.reservationId || flightId;
      if (reservationId) {
        const reservationLink = buildReservationUrl(reservationId);
        if (reservationLink) {
          router.push(reservationLink, undefined, { scroll: true });
        }
      }
    }
  }, [
    onCardClickOverride,
    flight,
    rawFlights,
    openModal,
    isShowingReturnFlights,
    responseStructure,
    selectDepartureFlight,
    isMobile,
    handleCombinedFlightPackageSelection,
    selectedDepartureFlight,
    router,
    buildReservationUrl,
    buildListingUrl,
  ]);
  // Gidiş-dönüş biletleri için özel render
  if (isRoundTrip) {
    return (
      <RoundTripFlightCard flight={flight} onCardClick={handleCardClick} />
    );
  }

  return (
    <div
      onClick={handleCardClick}
      className="border-border flex cursor-pointer flex-col gap-4 rounded-lg border bg-white p-6 hover:shadow-lg"
    >
      {/* Airline and Flight Times */}
      <div className="flex items-center justify-between">
        {/* Airline Logo and Departure */}
        <div className="flex w-full items-center justify-between gap-4">
          {/* Airline Logo */}
          {(() => {
            const airlineCode =
              flight?.AirlineCode ||
              flight?.airlineCode ||
              flight?.Airline ||
              flight?.Segments?.[0]?.MarketingAirline ||
              flight?.segments?.[0]?.MarketingAirline;
            const logoUrl = airlineCode ? getAirlineLogoUrl(airlineCode) : "";

            return (
              <div className="border-border-card-weak relative flex h-12 w-12 items-center justify-center rounded-sm border bg-white p-[1px]">
                <Image
                  src={logoUrl}
                  alt="Airline Logo"
                  fill
                  className="object-contain"
                />
              </div>
            );
          })()}
          <div className="flex w-full flex-col gap-1">
            {" "}
            <div className="flex flex-row items-center justify-between gap-2">
              <div className="text-foreground-primary min-w-[42px] text-lg font-semibold">
                {dateFormats.timeOnly(flight.OutgoingDepartureDateTime, locale)}
              </div>
              <div className="border-border-card w-full border-t"></div>
              <div className="bg-background-300 h-[26px] rounded-[4px] px-2 text-center">
                <span className="text-primary-muted-subtext text-center text-xs font-medium text-nowrap">
                  {normalizeFlightDuration(flight.FlightDuration)}
                </span>
              </div>
              <div className="border-border-card w-full border-t"></div>
              <div className="text-foreground-primary min-w-[42px] text-lg font-semibold">
                {dateFormats.timeOnly(flight.OutgoingArrivalDateTime, locale)}
              </div>
            </div>
            <div className="grid grid-cols-3 items-center">
              <div className="text-primary-muted-subtext min-w-0 truncate text-sm">
                {flight.Origin.Code}
              </div>
              <div className="text-primary-muted-subtext flex justify-center text-xs font-normal">
                {flight.StopText}
              </div>
              <div className="text-primary-muted-subtext min-w-0 truncate text-right text-sm">
                {flight.Destination.Code}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Route and Date */}
      <div className="flex items-center justify-start">
        <span className="text-foreground-primary text-sm font-normal">
          {flight.Origin.LocalizedCityName} -{" "}
          {flight.Destination.LocalizedCityName}
        </span>
        <div className="flex items-center">
          <div className="bg-border-card mx-2 h-4 w-px"></div>
          <span className="text-foreground-primary text-sm font-normal">
            {dateFormats.dayLongMonthShortDay(
              flight.OutgoingDepartureDateTime,
              locale,
            )}
          </span>
        </div>
      </div>
      <div className="border-border-card w-full border-t"></div>
      {/* Price and Action Button */}
      <div className="flex items-center justify-between">
        <div className="text-foreground text-xl font-semibold">
          {formatCurrency(flight.Price, currency || flight.Currency, {
            showSymbol: false,
            showCode: true,
          })}
        </div>
        <Button className="flex h-[36px] flex-row items-center px-4 lg:px-4">
          <span className="text-sm">{t("searchForTicket")}</span>
        </Button>
      </div>
    </div>
  );
}

// Gidiş-dönüş biletleri için özel component
function RoundTripFlightCard({ flight, onCardClick }) {
  const locale = useLocale();
  const { currency } = useCurrency();
  const t = useTranslationDetector("flight");
  return (
    <div
      onClick={onCardClick}
      className="border-border-weak hover:shadow-small flex cursor-pointer flex-col gap-4 rounded-lg border bg-white p-6"
    >
      {/* Gidiş Uçuşu */}
      <div className="flex flex-col gap-4 lg:gap-6">
        <div className="flex items-center justify-between space-x-4">
          {/* Airline Logo */}
          {(() => {
            const airlineCode =
              flight?.AirlineCode ||
              flight?.airlineCode ||
              flight?.Segments?.[0]?.MarketingAirline ||
              flight?.segments?.[0]?.MarketingAirline;
            const logoUrl = airlineCode ? getAirlineLogoUrl(airlineCode) : "";

            return (
              <div className="border-border-card-weak relative flex h-8 w-8 items-center justify-center rounded-sm border bg-white p-[1px]">
                <Image
                  src={logoUrl}
                  alt="Airline Logo"
                  fill
                  className="object-contain"
                />
              </div>
            );
          })()}

          <div className="flex w-full flex-col gap-1">
            <div className="flex flex-row items-center justify-between gap-2">
              <div className="text-foreground-primary text-base font-semibold">
                {dateFormats.timeOnly(flight.OutgoingDepartureDateTime, locale)}
              </div>
              <div className="border-border-card w-full border-t"></div>
              <div className="bg-background-300 min-w-16 rounded-lg px-2 py-[2px] text-center">
                <span className="text-foreground-muted text-center text-xs font-medium">
                  {normalizeFlightDuration(flight.FlightDuration)}
                </span>
              </div>
              <div className="border-border-card w-full border-t"></div>
              <div className="text-foreground-primary text-base font-semibold">
                {dateFormats.timeOnly(flight.OutgoingArrivalDateTime, locale)}
              </div>
            </div>
            <div className="grid grid-cols-3 items-center">
              <div className="text-primary-muted-subtext min-w-0 truncate text-sm">
                {flight.Origin.Name}
              </div>
              <div className="text-primary-muted-subtext flex justify-center text-xs font-normal">
                {flight.StopText}
              </div>
              <div className="text-primary-muted-subtext min-w-0 truncate text-right text-sm">
                {flight.Destination.Name}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-start">
          <span className="text-foreground-primary text-sm font-normal">
            {flight.Origin.LocalizedCityName} -{" "}
            {flight.Destination.LocalizedCityName} |{" "}
            {dateFormats.dayLongMonthShortDay(
              flight.OutgoingDepartureDateTime,
              locale,
            )}
          </span>
        </div>
      </div>

      {/* Ayırıcı çizgi */}
      <div className="border-border-card w-full border-t"></div>

      {/* Dönüş Uçuşu */}
      <div className="space-y-3">
        <div className="flex items-center justify-between space-x-4">
          {/* Airline Logo */}
          {(() => {
            const airlineCode =
              flight?.AirlineCode ||
              flight?.airlineCode ||
              flight?.Segments?.[0]?.MarketingAirline ||
              flight?.segments?.[0]?.MarketingAirline;
            const logoUrl = airlineCode ? getAirlineLogoUrl(airlineCode) : "";

            return (
              <div className="border-border-card-weak relative flex h-8 w-8 items-center justify-center rounded-sm border bg-white p-[1px]">
                <Image
                  src={logoUrl}
                  alt="Airline Logo"
                  fill
                  className="object-contain"
                />
              </div>
            );
          })()}

          <div className="flex w-full flex-col gap-1">
            <div className="flex flex-row items-center justify-between gap-2">
              <div className="text-foreground-primary text-base font-semibold">
                {dateFormats.timeOnly(flight.IncomingDepartureDateTime, locale)}
              </div>
              <div className="border-border-card w-full border-t"></div>
              <div className="bg-background-300 min-w-16 rounded-lg px-2 py-[2px] text-center">
                <span className="text-foreground-muted text-center text-xs font-medium">
                  {normalizeFlightDuration(flight.FlightDuration)}
                </span>
              </div>
              <div className="border-border-card w-full border-t"></div>
              <div className="text-foreground-primary text-base font-semibold">
                {dateFormats.timeOnly(
                  flight.IncomingArrivalDateTime ||
                    flight.IncomingDepartureDateTime,
                  locale,
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 items-center">
              <div className="text-primary-muted-subtext min-w-0 truncate text-sm">
                {flight.Destination.Name}
              </div>
              <div className="text-primary-muted-subtext flex justify-center text-xs font-normal">
                {flight.StopText}
              </div>
              <div className="text-primary-muted-subtext min-w-0 truncate text-right text-sm">
                {flight.Origin.Name}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Route and Date */}
      <div className="flex items-center justify-start">
        <span className="text-foreground-primary text-sm font-normal">
          {flight.Destination.LocalizedCityName} -{" "}
          {flight.Origin.LocalizedCityName} |{" "}
          {dateFormats.dayLongMonthShortDay(
            flight.IncomingDepartureDateTime,
            locale,
          )}
        </span>
      </div>

      <div className="border-border-card w-full border-t"></div>

      {/* Price and Action Button */}
      <div className="flex items-center justify-between">
        <div className="text-foreground text-xl font-semibold">
          {formatCurrency(flight.Price, currency || flight.Currency, {
            showSymbol: false,
            showCode: true,
          })}
        </div>
        <Button className="flex h-[36px] flex-row items-center px-4 lg:px-4">
          <span className="text-sm">{t("searchForTicket")}</span>
        </Button>
      </div>
    </div>
  );
}
